
import React from 'react';
import cn from 'classnames';
import styles from './dropdown.module.css';

export type DropdownOption<T> = {
  value: string | number | readonly string[] | undefined,
  text: string,
};

export type Props<T> = {
  id: string,
  name?: string,
  placeholder?: string,
  value?: string | number | readonly string[] | undefined,
  error?: boolean,
  options: DropdownOption<T>[],
  onChange: (value: string | number | readonly string[] | undefined) => void,
  className?: string,
  disabled?: boolean,
};

const Dropdown = <T,>({
  id,
  name,
  placeholder,
  value,
  error = false,
  options = [],
  onChange,
  className,
  disabled = false,
}: Props<T>) => {
  const handleOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const option = options.find(option => String(option.value) === e.currentTarget.value);
    onChange(option?.value);
  };

  const placeholderId = `${id}-placeholder`;

  const isValueInOptions = options.some(option => option.value === value);

  const valueStyle = isValueInOptions
    ? styles.presentValue
    : styles.absentValue;

  return (
    <select
      id={id}
      name={name || id}
      className={cn(
        'form-control',
        styles.dropdown,
        error ? styles.error : '',
        disabled ? styles.disabled : '',
        valueStyle,
        className,
      )}
      value={value ?? (placeholder ? '' : options?.[0]?.value)}
      onChange={handleOnChange}
      disabled={disabled}
    >
      {placeholder && (
        <option id={placeholderId} key={placeholderId} value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((option, index) => (
        <option
          id={`${id}-${index}`}
          key={`${index}-${option.text}`}
          value={option.value}
        >
          {option.text}
        </option>
      ))}
    </select>
  );
};

export default Dropdown;